import React, { useState, useEffect } from "react";
import { useWorkspace } from "../WorkspaceContext";
import { useAuth } from "../AuthContext";
import { http } from "../api";

const Check = ({ s = 11 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2"><path d="m5 12 5 5L20 6" /></svg>
);
const Arrow = ({ s = 16 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

const STAGES = [
  { id: "researching", name: "Researching", color: "var(--indigo-300)" },
  { id: "preparing", name: "Preparing", color: "var(--indigo)" },
  { id: "applied", name: "Applied", color: "var(--indigo-deep)" },
  { id: "offer", name: "Got offer", color: "var(--ok)" },
];

function tierClass(tier) {
  const t = (tier || "").toLowerCase();
  if (/good|target|match/.test(t)) return "good";
  if (/back|safety/.test(t)) return "back";
  return "amb";
}

export default function Dashboard({ go }) {
  const { universities, tasks, updateTask } = useWorkspace();
  const { user } = useAuth();
  const firstName = (user?.name || "there").split(" ")[0];

  const openTasks = tasks.filter((t) => !t.done);
  const todayTasks = openTasks.slice(0, 4);

  const nextUni =
    universities.find((u) => u.deadlineUrgent) ||
    universities.find((u) => u.status === "preparing") ||
    universities[0];

  const deadlineUnis = [...universities]
    .filter((u) => u.deadline)
    .sort((a, b) => (b.deadlineUrgent ? 1 : 0) - (a.deadlineUrgent ? 1 : 0))
    .slice(0, 3);

  const toggle = (t) => updateTask(t.id, { done: !t.done });

  return (
    <div className="vwrap" data-testid="dashboard">
      <div className="vhi">
        <h1>Namaste, <em>{firstName}</em>.</h1>
        <p>
          {openTasks.length > 0
            ? <>You have <strong>{openTasks.length} open task{openTasks.length > 1 ? "s" : ""}</strong> across <strong>{universities.length} universit{universities.length === 1 ? "y" : "ies"}</strong>. Take it one at a time.</>
            : <>You're all caught up across <strong>{universities.length} universit{universities.length === 1 ? "y" : "ies"}</strong>. Nice work.</>}
        </p>
      </div>

      {nextUni ? (
        <div className="vnext" data-testid="home-next-card">
          <div className="nl">
            <div className="tagrow">
              <span className="tag">Do this next</span>
              {nextUni.deadline && <span className="when">Deadline {nextUni.deadline}</span>}
            </div>
            <h2>Keep moving on <em>{nextUni.short || nextUni.name}</em>{nextUni.program ? <> — {nextUni.program}</> : null}.</h2>
            <div className="s">
              {typeof nextUni.progress === "number"
                ? `You're ${nextUni.progress}% through the checklist. Open it to see what's left.`
                : "Open the university page to see your checklist, costs and professors."}
            </div>
          </div>
          <button className="nbtn" onClick={() => go("detail", nextUni.id)} data-testid="home-next-open">
            Open {nextUni.short || nextUni.name.slice(0, 12)} <Arrow s={16} />
          </button>
        </div>
      ) : (
        <div className="vnext" data-testid="home-next-empty">
          <div className="nl">
            <div className="tagrow"><span className="tag">Get started</span></div>
            <h2>Add your first university to <em>start your journey</em>.</h2>
            <div className="s">Vidhya will research it live and build your tailored checklist.</div>
          </div>
          <button className="nbtn" onClick={() => go("board")} data-testid="home-add-first">Add a university <Arrow s={16} /></button>
        </div>
      )}

      <div className="vsec-h"><h3>Your journey</h3><span className="lk" onClick={() => go("board")} data-testid="home-open-board">Open the board →</span></div>
      <div className="vtrack">
        {STAGES.map((st) => {
          const n = universities.filter((u) => u.status === st.id).length;
          const width = n === 0 ? 8 : Math.min(100, 28 + n * 22);
          return (
            <div className="vstage" key={st.id} onClick={() => go("board")} data-testid={`home-stage-${st.id}`}>
              <span className="tick" style={{ background: st.color }} />
              <div className="num" style={{ color: st.color }}>{n}</div>
              <div className="nm">{st.name}</div>
              <div className="bar"><i style={{ width: `${width}%`, background: st.color }} /></div>
            </div>
          );
        })}
      </div>

      <div className="vcols">
        <div className="vcard" data-testid="home-deadlines">
          <div className="vcard-h"><h3>Closest deadlines</h3><span className="mt">Tap to open</span></div>
          {deadlineUnis.length === 0 ? (
            <div className="vempty">No deadlines yet. Add a university to track them.</div>
          ) : (
            deadlineUnis.map((u) => (
              <div className="vuni" key={u.id} onClick={() => go("detail", u.id)} data-testid={`home-deadline-${u.id}`}>
                <div className="vulogo">{u.short?.[0] || u.name[0]}</div>
                <div className="vuinfo">
                  <b>{u.name}</b>
                  <span>{u.program || u.loc || "—"}</span>
                </div>
                <div className="vuright">
                  {u.tier && <span className={`vtier ${tierClass(u.tier)}`}>{u.tier}</span>}
                  <div className={`vdue ${u.deadlineUrgent ? "soon" : ""}`}>{u.deadline}</div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="vcard" data-testid="home-today">
          <div className="vcard-h"><h3>Today</h3><span className="mt">{openTasks.length} open</span></div>
          {todayTasks.length === 0 ? (
            <div className="vempty">Nothing on deck. Enjoy the calm.</div>
          ) : (
            todayTasks.map((t) => (
              <div className={`vtodo ${t.done ? "done" : ""}`} key={t.id} onClick={() => toggle(t)} data-testid={`home-task-${t.id}`}>
                <div className="vchk"><Check s={11} /></div>
                <div className="vtb">
                  <b>{t.title}</b>
                  <span>
                    {t.uni ? <>For {t.uni}</> : "Task"}{t.due ? ` · ${t.due}` : ""}.{" "}
                    <span className="for" onClick={(e) => { e.stopPropagation(); go("chat"); }}>Work with Vidhya</span>
                  </span>
                </div>
              </div>
            ))
          )}
          <div className="vsee"><span className="lk" onClick={() => go("tasks")} data-testid="home-see-all-tasks">See all tasks →</span></div>
        </div>
      </div>
    </div>
  );
}
